import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { fetchGroupStandings } from '../../../../../apis/class';
import { CategoryStanding } from '../../../../../apis/tournament';
import { formatTeamCategoryLabel } from '../../../../../utils/tournamentCategoryTemplate';
import { CustomButton } from '../../../../../components';

interface TeamStanding {
  id: number;
  name: string;
  countMatches: number;
  matchWin: number;
  gameWin: number;
  pointsDiff: number;
  pointsWon: number;
  matchLoss?: number;
  teamWin?: number;
  teamDraw?: number;
  teamLoss?: number;
  teamPoints?: number;
  countTeamMeetings?: number;
}

interface GroupStandingsProps {
  classId: number;
  groupId: number;
  roundNumber?: number;
  categoryStandings?: CategoryStanding[];
  isTeamsClass?: boolean;
  rankingUsesTeamPoints?: boolean;
}

function formatOptionalStandingStat(value: number | undefined | null): string {
  if (value === null || value === undefined) return '—';
  return String(value);
}

export const GroupStandings: FC<GroupStandingsProps> = ({
  classId,
  groupId,
  roundNumber = 0,
  categoryStandings,
  isTeamsClass = false,
  rankingUsesTeamPoints = false,
}) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  const useCategoryData =
    isTeamsClass && selectedCategoryId != null && Boolean(categoryStandings?.length);

  const showMatchupWdl = isTeamsClass && !useCategoryData;
  const showMatchupPts = showMatchupWdl && rankingUsesTeamPoints;

  const categoryTeams = useMemo(() => {
    if (!useCategoryData || selectedCategoryId == null) return null;
    const category = categoryStandings!.find((c) => c.tournamentCategoryId === selectedCategoryId);
    const group = category?.groups.find((g) => g.groupId === groupId);
    if (!group) return [];
    return group.teams.map((team) => {
      const row = team as TeamStanding & { points?: number };
      return {
        ...row,
        pointsDiff: row.pointsDiff ?? row.points ?? 0,
        pointsWon: row.pointsWon ?? 0,
        matchLoss: row.countMatches - row.matchWin,
      };
    });
  }, [useCategoryData, selectedCategoryId, categoryStandings, groupId]);

  const loadStandings = useCallback(async () => {
    if (useCategoryData) return;
    setIsLoading(true);
    try {
      const data = await fetchGroupStandings(classId, groupId, roundNumber);
      const standingsWithLosses = data.map((team: TeamStanding) => ({
        ...team,
        matchLoss: team.countMatches - team.matchWin,
      }));
      setStandings(standingsWithLosses);
    } catch (error) {
      console.error('Failed to fetch group standings', error);
    } finally {
      setIsLoading(false);
    }
  }, [classId, groupId, roundNumber, useCategoryData]);

  useEffect(() => {
    if (!isExpanded) return;
    if (useCategoryData && categoryTeams) {
      setStandings(categoryTeams);
      return;
    }
    loadStandings();
  }, [isExpanded, loadStandings, useCategoryData, categoryTeams]);

  const displayStandings = useCategoryData && categoryTeams ? categoryTeams : standings;

  if (!isExpanded) {
    return (
      <div className="my-2">
        <CustomButton
          variant="link"
          btnText={t(prefix + 'standingsShow')}
          cb={() => setIsExpanded(true)}
        />
      </div>
    );
  }

  return (
    <div className="my-3 p-3 border rounded">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="font-weight-bold mb-0">{t(prefix + 'standingsGroupTitle')}</h6>
        <div>
          <CustomButton
            variant="link"
            btnText={t(prefix + 'standingsHide')}
            cb={() => setIsExpanded(false)}
          />
          {!useCategoryData && (
            <CustomButton
              variant="outline-secondary"
              size="sm"
              btnText={isLoading ? t(prefix + 'standingsLoading') : t(prefix + 'standingsRefresh')}
              cb={loadStandings}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
      {isTeamsClass && (categoryStandings?.length ?? 0) > 0 && (
        <Form.Group className="mb-2">
          <Form.Label className="small mb-1">{t(prefix + 'standingsSelectCategory')}</Form.Label>
          <Form.Select
            size="sm"
            value={selectedCategoryId ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedCategoryId(val === '' ? null : Number(val));
            }}
          >
            <option value="">{t(prefix + 'standingsOverall')}</option>
            {categoryStandings!.map((c) => (
              <option key={c.tournamentCategoryId} value={c.tournamentCategoryId}>
                {formatTeamCategoryLabel(
                  t,
                  { name: c.categoryName, categoryTemplate: c.categoryTemplate },
                  c.categoryName,
                )}
              </option>
            ))}
          </Form.Select>
          <p className="small text-muted mb-0 mt-1">{t(prefix + 'standingsCategoryHint')}</p>
        </Form.Group>
      )}
      <div className="table-responsive">
        <table
          className="table table-sm table-striped mb-0"
          style={showMatchupWdl ? { minWidth: '60rem' } : undefined}
        >
          <thead>
            <tr>
              <th>#</th>
              <th>{t(prefix + 'standingsTeam')}</th>
              {showMatchupPts ? (
                <th>
                  <span className="d-inline-flex align-items-center gap-1">
                    {t(prefix + 'standingsPts')}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{t(prefix + 'standingsTeamPointsTooltip')}</Tooltip>}
                    >
                      <InfoCircleFill size={12} className="text-muted" />
                    </OverlayTrigger>
                  </span>
                </th>
              ) : null}
              {showMatchupWdl ? (
                <>
                  <th>
                    <span className="d-inline-flex align-items-center gap-1">
                      {t(prefix + 'standingsW')}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t(prefix + 'standingsWdlTooltip')}</Tooltip>}
                      >
                        <InfoCircleFill size={12} className="text-muted" />
                      </OverlayTrigger>
                    </span>
                  </th>
                  <th>{t(prefix + 'standingsD')}</th>
                  <th>{t(prefix + 'standingsL')}</th>
                </>
              ) : null}
              <th>
                <span className="d-inline-flex align-items-center gap-1">
                  {t(prefix + 'standingsPlayed')}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{t(prefix + 'standingsPlayedTooltip')}</Tooltip>}
                  >
                    <InfoCircleFill size={12} className="text-muted" />
                  </OverlayTrigger>
                </span>
              </th>
              <th>
                <span className="d-inline-flex align-items-center gap-1">
                  {t(prefix + 'standingsWon')}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{t(prefix + 'standingsWonTooltip')}</Tooltip>}
                  >
                    <InfoCircleFill size={12} className="text-muted" />
                  </OverlayTrigger>
                </span>
              </th>
              <th>{t(prefix + 'standingsLost')}</th>
              <th>{t(prefix + 'standingsGamesWon')}</th>
              <th>{t(prefix + 'standingsPointsDiff')}</th>
              <th>{t(prefix + 'standingsPointsFor')}</th>
            </tr>
          </thead>
          <tbody>
            {displayStandings.map((team, index) => (
              <tr key={team.id}>
                <td>{index + 1}</td>
                <td>{team.name}</td>
                {showMatchupPts ? <td>{formatOptionalStandingStat(team.teamPoints)}</td> : null}
                {showMatchupWdl ? (
                  <>
                    <td>{formatOptionalStandingStat(team.teamWin)}</td>
                    <td>{formatOptionalStandingStat(team.teamDraw)}</td>
                    <td>{formatOptionalStandingStat(team.teamLoss)}</td>
                  </>
                ) : null}
                <td>{team.countMatches}</td>
                <td>{team.matchWin}</td>
                <td>{team.matchLoss}</td>
                <td>{team.gameWin}</td>
                <td>{team.pointsDiff}</td>
                <td>{team.pointsWon}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
